// @ts-nocheck
const BASE = '/api/wht';

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const isFormData = options?.body instanceof FormData;
  const headers: Record<string, string> = isFormData
    ? {} // let browser set Content-Type with boundary for FormData
    : { 'Content-Type': 'application/json' };

  const res = await fetch(`${BASE}${path}`, {
    credentials: 'include',
    ...options,
    headers: { ...headers, ...options?.headers },
  });

  if (res.status === 401 && !path.includes('/auth/')) {
    window.location.href = '/login';
    throw new Error('Unauthorized');
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error((err as { error?: string }).error || res.statusText);
  }

  // Handle CSV responses
  if (res.headers.get('content-type')?.includes('text/csv')) {
    return (await res.text()) as unknown as T;
  }

  return res.json() as Promise<T>;
}

export const api = {
  // Auth
  login: (email: string, totp_code: string) =>
    request<{ user: { id: number; email: string; name: string; role: string } }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, totp_code }),
    }),
  logout: () => request('/auth/logout', { method: 'POST' }),
  me: () => request<{ user: { userId: number; email: string; name: string; role: string } }>('/auth/me'),

  // Dashboard
  dashboardSummary: (year?: number) =>
    request<Record<string, unknown>>(`/dashboard/summary${year ? `?year=${year}` : ''}`),
  dashboardPriority: (year?: number, limit = 20) =>
    request<{ priority: Record<string, unknown>[] }>(`/dashboard/priority?limit=${limit}${year ? `&year=${year}` : ''}`),
  recentActivity: () => request<Record<string, unknown>>('/dashboard/recent-activity'),
  availableYears: () => request<{ years: number[]; defaultYear: number }>('/dashboard/years'),

  // Import
  uploadXlsx: (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return request<Record<string, unknown>>('/import/upload', {
      method: 'POST',
      body: fd,
      headers: {},
    });
  },
  confirmImport: (file: File, filename: string, year: number, clientOverrides?: Record<string, number>) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('filename', filename);
    fd.append('year', String(year));
    if (clientOverrides) fd.append('clientOverrides', JSON.stringify(clientOverrides));
    return request<Record<string, unknown>>('/import/confirm', { method: 'POST', body: fd, headers: {} });
  },

  // Clients
  clients: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return request<{ clients: Record<string, unknown>[]; total: number }>(`/clients${qs}`);
  },
  clientDetail: (id: number, year?: number | string) =>
    request<{ client: Record<string, unknown>; transactions: Record<string, unknown>[] }>(
      `/clients/${id}${year ? `?year=${year}` : ''}`
    ),
  updateClient: (id: number, data: Record<string, unknown>) =>
    request<{ client: Record<string, unknown> }>(`/clients/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  clientEmails: (id: number) =>
    request<{ emails: Record<string, unknown>[] }>(`/clients/${id}/emails`),

  // Transactions
  transactions: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return request<{ transactions: Record<string, unknown>[]; total: number }>(`/transactions${qs}`);
  },
  updateTransaction: (id: number, data: Record<string, unknown>) =>
    request<{ transaction: Record<string, unknown> }>(`/transactions/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  bulkUpdateTransactions: (transactionIds: number[], cert_status: string, cert_notes?: string) =>
    request<{ results: Record<string, unknown>[] }>('/transactions/bulk', {
      method: 'PUT',
      body: JSON.stringify({ transactionIds, cert_status, cert_notes }),
    }),
  uploadCert: (transactionId: number, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return request<Record<string, unknown>>(`/transactions/${transactionId}/upload-cert`, {
      method: 'POST',
      body: fd,
      headers: {},
    });
  },

  // Email
  sendEmail: (
    clientId: number,
    templateName?: string,
    year?: string,
    custom?: { customSubject: string; customBody: string }
  ) =>
    request<Record<string, unknown>>('/email/send', {
      method: 'POST',
      body: JSON.stringify({ clientId, templateName, year, ...(custom || {}) }),
    }),
  previewEmail: (clientId: number, templateName?: string, year?: string) =>
    request<{ to: string; subject: string; body: string; transactionCount: number; totalAmount: number }>(
      '/email/preview',
      { method: 'POST', body: JSON.stringify({ clientId, templateName, year }) }
    ),
  bulkSendEmail: (
    clientIds: number[],
    templateName?: string,
    year?: string,
    drafts?: { clientId: number; subject: string; body: string }[]
  ) =>
    request<Record<string, unknown>>('/email/bulk-send', {
      method: 'POST',
      body: JSON.stringify({ clientIds, templateName, year, drafts }),
    }),
  testEmail: (data: {
    to?: string;
    templateName?: string;
    clientId?: number;
    customSubject?: string;
    customBody?: string;
  }) =>
    request<{ success: boolean; sentTo: string; messageId?: string }>('/email/test', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  emailTemplates: () => request<{ templates: Record<string, unknown>[] }>('/email/templates'),
  emailTemplate: (name: string) =>
    request<{ template: Record<string, unknown> }>(`/email/templates/${encodeURIComponent(name)}`),
  updateEmailTemplate: (name: string, data: { subject: string; body: string }) =>
    request<{ template: Record<string, unknown> }>(`/email/templates/${encodeURIComponent(name)}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  emailHistory: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return request<{ emails: Record<string, unknown>[] }>(`/email/history${qs}`);
  },

  // Reports
  reportSummary: (year?: number) =>
    request<Record<string, unknown>>(`/reports/summary${year ? `?year=${year}` : ''}`),
  exportCsv: (year?: number, status?: string) => {
    const params = new URLSearchParams();
    if (year) params.set('year', String(year));
    if (status) params.set('status', status);
    return request<string>(`/reports/export?${params.toString()}`);
  },
};
